import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  ActionModal,
  NoticeboardButton,
  ACTION_MODAL_FOOTER_PRIMARY_SIZE,
} from "../../shared";
import type {
  ChildExtracurricular,
  ChildFeeAccount,
  FeeAccountCategory,
  FeeBillingPattern,
} from "../../../types/parentFamily";
import { formatCurrency } from "../../../utils/parentFamilyHelpers";
import { defaultEndDateForOneMonth } from "../../../utils/feeAccountHelpers";
import {
  todayIsoDate,
  parseIsoDate,
  toIsoDate,
} from "../../../utils/familyDateUtils";
import {
  EVENT_DATE_PICKER_FORMAT,
  EVENT_DATE_PICKER_LOCALE,
} from "../../../utils/eventDateDisplay";
import { FAMILY_OUTLINED_FIELD_SX } from "./familyStyles";

export interface FeeAccountFormValues {
  id?: string;
  title: string;
  category: FeeAccountCategory;
  billingPattern: FeeBillingPattern;
  startDate: string;
  endDate: string;
  oneMonthOnly: boolean;
  totalAmount: string;
  dueDate: string;
  monthlyAmount: string;
  linkedActivityId?: string;
}

interface FeeAccountFormModalProps {
  open: boolean;
  onClose: () => void;
  initialAccount?: ChildFeeAccount | null;
  /** Activities from the Extracurricular tab for this child. */
  extracurricularActivities?: ChildExtracurricular[];
  /** Activity IDs already linked to another charge (exclude unless editing). */
  billedActivityIds?: string[];
  onSave: (values: FeeAccountFormValues) => void | Promise<void>;
}

const emptyForm = (): FeeAccountFormValues => ({
  title: "",
  category: "event",
  billingPattern: "once-off",
  startDate: todayIsoDate(),
  endDate: "",
  oneMonthOnly: false,
  totalAmount: "",
  dueDate: "",
  monthlyAmount: "",
});

function categoryForPattern(pattern: FeeBillingPattern): FeeAccountCategory {
  return pattern === "recurring-fixed" ? "extracurricular" : "event";
}

export const FeeAccountFormModal: React.FC<FeeAccountFormModalProps> = ({
  open,
  onClose,
  initialAccount,
  extracurricularActivities = [],
  billedActivityIds = [],
  onSave,
}) => {
  const isEdit = Boolean(initialAccount);
  const [form, setForm] = useState<FeeAccountFormValues>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const availableActivities = useMemo(() => {
    const editingActivityId = initialAccount?.linkedActivityId;
    return extracurricularActivities.filter(
      (activity) =>
        !billedActivityIds.includes(activity.id) ||
        activity.id === editingActivityId
    );
  }, [extracurricularActivities, billedActivityIds, initialAccount?.linkedActivityId]);

  useEffect(() => {
    if (!open) return;
    if (initialAccount) {
      const oneMonth =
        initialAccount.billingPattern === "recurring-fixed" &&
        Boolean(initialAccount.endDate) &&
        initialAccount.endDate?.slice(0, 7) === initialAccount.startDate.slice(0, 7);
      setForm({
        id: initialAccount.id,
        title: initialAccount.title,
        category: initialAccount.category,
        billingPattern: initialAccount.billingPattern,
        startDate: initialAccount.startDate.slice(0, 10),
        endDate: initialAccount.endDate?.slice(0, 10) || "",
        oneMonthOnly: oneMonth,
        totalAmount:
          initialAccount.totalAmount != null
            ? String(initialAccount.totalAmount)
            : "",
        dueDate: initialAccount.dueDate?.slice(0, 10) || "",
        monthlyAmount:
          initialAccount.monthlyAmount != null
            ? String(initialAccount.monthlyAmount)
            : "",
        linkedActivityId: initialAccount.linkedActivityId,
      });
    } else {
      setForm(emptyForm());
    }
    setError(null);
    setSaving(false);
  }, [open, initialAccount]);

  const previewMonthly = useMemo(() => {
    const amount = Number.parseFloat(form.monthlyAmount.replace(/,/g, ""));
    return Number.isFinite(amount) && amount > 0 ? amount : null;
  }, [form.monthlyAmount]);

  const applyActivity = (activityId: string) => {
    if (!activityId) {
      setForm((f) => ({
        ...f,
        linkedActivityId: undefined,
      }));
      return;
    }
    const activity = extracurricularActivities.find((a) => a.id === activityId);
    if (!activity) return;
    setForm((f) => ({
      ...f,
      linkedActivityId: activity.id,
      title: activity.name,
      category: "extracurricular",
      monthlyAmount:
        activity.amount != null ? String(activity.amount) : f.monthlyAmount,
    }));
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      setError("Enter a title for this charge.");
      return;
    }
    if (!form.startDate) {
      setError("Select a start date.");
      return;
    }

    let endDate = form.endDate.trim();
    if (form.billingPattern === "recurring-fixed" && form.oneMonthOnly) {
      endDate = defaultEndDateForOneMonth(form.startDate);
    }

    if (form.billingPattern === "once-off") {
      const total = Number.parseFloat(form.totalAmount.replace(/,/g, ""));
      if (!Number.isFinite(total) || total <= 0) {
        setError("Enter a valid amount.");
        return;
      }
    } else {
      const monthly = Number.parseFloat(form.monthlyAmount.replace(/,/g, ""));
      if (!Number.isFinite(monthly) || monthly <= 0) {
        setError("Enter a valid monthly amount.");
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      await onSave({
        ...form,
        endDate,
        category: categoryForPattern(form.billingPattern),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save charge.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit charge" : "Add charge"}
      maxWidth="sm"
      footer={
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
          <NoticeboardButton variant="secondary" size={ACTION_MODAL_FOOTER_PRIMARY_SIZE} onClick={onClose}>
            Cancel
          </NoticeboardButton>
          <NoticeboardButton
            variant="primary"
            size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
            loading={saving}
            disabled={saving}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {isEdit ? "Save changes" : "Add charge"}
          </NoticeboardButton>
        </Box>
      }
    >
      <Stack spacing={2} sx={FAMILY_OUTLINED_FIELD_SX}>
        <FormControl fullWidth size="small">
          <InputLabel id="fee-account-pattern-label">Billing</InputLabel>
          <Select
            labelId="fee-account-pattern-label"
            label="Billing"
            value={form.billingPattern}
            onChange={(e) => {
              const billingPattern = e.target.value as FeeBillingPattern;
              setForm((f) => ({
                ...f,
                billingPattern,
                category: categoryForPattern(billingPattern),
                linkedActivityId:
                  billingPattern === "once-off" ? undefined : f.linkedActivityId,
              }));
            }}
          >
            <MenuItem value="once-off">Once off / Event</MenuItem>
            <MenuItem value="recurring-fixed">Extracurricular (monthly)</MenuItem>
          </Select>
        </FormControl>

        {form.billingPattern === "recurring-fixed" ? (
          <>
            <FormControl fullWidth size="small">
              <InputLabel id="fee-account-activity-label">
                Extracurricular activity
              </InputLabel>
              <Select
                labelId="fee-account-activity-label"
                label="Extracurricular activity"
                value={form.linkedActivityId || ""}
                onChange={(e) => applyActivity(String(e.target.value))}
              >
                <MenuItem value="">
                  <em>Custom (enter manually)</em>
                </MenuItem>
                {availableActivities.map((activity) => (
                  <MenuItem key={activity.id} value={activity.id}>
                    {activity.name}
                    {activity.amount != null && activity.amount > 0
                      ? ` · ${formatCurrency(activity.amount, activity.currency)}`
                      : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {extracurricularActivities.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No activities yet — add them on the Extracurricular tab first, or
                choose Custom above.
              </Typography>
            ) : null}
          </>
        ) : null}

        <TextField
          label="Title"
          fullWidth
          size="small"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder={
            form.billingPattern === "once-off"
              ? "e.g. Deb's Ball 2026"
              : "e.g. Swimming, Ballet"
          }
        />

        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={EVENT_DATE_PICKER_LOCALE}
        >
          <DatePicker
            label="Start date"
            format={EVENT_DATE_PICKER_FORMAT}
            value={parseIsoDate(form.startDate)}
            onChange={(date) => {
              const startDate = toIsoDate(date);
              setForm((f) => ({
                ...f,
                startDate,
                endDate:
                  f.oneMonthOnly && f.billingPattern === "recurring-fixed"
                    ? defaultEndDateForOneMonth(startDate)
                    : f.endDate,
              }));
            }}
            slotProps={{ textField: { fullWidth: true, size: "small" } }}
          />
        </LocalizationProvider>

        {form.billingPattern === "recurring-fixed" ? (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.oneMonthOnly}
                  onChange={(e) => {
                    const oneMonthOnly = e.target.checked;
                    setForm((f) => ({
                      ...f,
                      oneMonthOnly,
                      endDate: oneMonthOnly
                        ? defaultEndDateForOneMonth(f.startDate)
                        : "",
                    }));
                  }}
                />
              }
              label="One month only"
            />
            {!form.oneMonthOnly ? (
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={EVENT_DATE_PICKER_LOCALE}
              >
                <DatePicker
                  label="End date (optional)"
                  format={EVENT_DATE_PICKER_FORMAT}
                  value={parseIsoDate(form.endDate)}
                  onChange={(date) =>
                    setForm((f) => ({ ...f, endDate: toIsoDate(date) }))
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      helperText: "Leave blank for ongoing until cancelled",
                    },
                  }}
                />
              </LocalizationProvider>
            ) : null}
            <TextField
              label="Monthly amount (ZAR)"
              fullWidth
              size="small"
              type="number"
              inputProps={{ min: 0, step: "0.01" }}
              value={form.monthlyAmount}
              onChange={(e) =>
                setForm((f) => ({ ...f, monthlyAmount: e.target.value }))
              }
              helperText={
                previewMonthly != null
                  ? `Each billable month: ${formatCurrency(previewMonthly, "ZAR")}`
                  : undefined
              }
            />
          </>
        ) : (
          <>
            <TextField
              label="Amount (ZAR)"
              fullWidth
              size="small"
              type="number"
              inputProps={{ min: 0, step: "0.01" }}
              value={form.totalAmount}
              onChange={(e) =>
                setForm((f) => ({ ...f, totalAmount: e.target.value }))
              }
            />
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={EVENT_DATE_PICKER_LOCALE}
            >
              <DatePicker
                label="Due date (optional)"
                format={EVENT_DATE_PICKER_FORMAT}
                value={parseIsoDate(form.dueDate)}
                onChange={(date) =>
                  setForm((f) => ({ ...f, dueDate: toIsoDate(date) }))
                }
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </LocalizationProvider>
          </>
        )}

        {error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : null}
      </Stack>
    </ActionModal>
  );
};

export default FeeAccountFormModal;
